import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner, Alert, Card, Table, Badge, Modal, Row, Col } from "react-bootstrap";
import OrderForm from "../components/OrderForm";
import { ordersApi } from "../services/services";
import { Order, ClientOrderLog, OrderEditHistory } from "../types";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [logs, setLogs] = useState<ClientOrderLog[]>([]);
  const [editHistory, setEditHistory] = useState<OrderEditHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPreviousVersionModal, setShowPreviousVersionModal] = useState(false);
  const [previousVersionData, setPreviousVersionData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (id) {
      setLoading(true);
      ordersApi.getOrder(parseInt(id))
        .then((orderData) => {
          setOrder(orderData);
          return Promise.all([
            ordersApi.getClientOrderLogs(parseInt(id))
              .then((logsData) => setLogs(logsData))
              .catch(() => setLogs([])),
            ordersApi.getEditHistory(parseInt(id))
              .then((historyData) => setEditHistory(historyData))
              .catch(() => setEditHistory([])),
          ]);
        })
        .catch((err) =>
          setError(err.message || "Order not found or server error")
        )
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleViewPreviousVersion = (historyRecord: OrderEditHistory) => {
    try {
      if (historyRecord.previousFormData) {
        setPreviousVersionData(JSON.parse(historyRecord.previousFormData));
      } else {
        setPreviousVersionData({});
      }
    } catch {
      setPreviousVersionData({});
    }
    setShowPreviousVersionModal(true);
  };

  const handlePrint = () => window.print();

  if (loading)
    return (
      <div className="text-center p-4">
        <Spinner animation="border" />
      </div>
    );
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!order) return <Alert variant="warning">Order not found</Alert>;

  return (
    <>
      <PageContainer>
        <PageHeader title="Manage" subTitle="Order Details" />
        <div className="no-print d-flex justify-content-between gap-2 mb-3">
          <Button variant="secondary" onClick={() => navigate("/orders")}>
            <i className="bi bi-arrow-left me-1"></i> Back to Orders
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            <i className="bi bi-printer me-1"></i> Print
          </Button>
        </div>

        <OrderForm order={order} />

        {/* Order History Section */}
        <Card className="mt-4 shadow-sm border-0 mb-5">
          <Card.Header className="bg-white py-3 border-bottom-0">
            <h5 className="mb-0 text-dark">Order History</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {logs.length === 0 ? (
              <p className="text-muted text-center my-3">
                No status changes recorded yet.
              </p>
            ) : (
              <Table className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 ps-4">Activity</th>
                    <th className="border-0">Done by</th>
                    <th className="border-0">Date Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    let badgeClass = "secondary";
                    const status = log.newStatus || "";
                    const notes = log.notes || "";
                    const isEditLog = notes.includes("edited by");

                    if (status.includes("Confirmed")) badgeClass = "success";
                    else if (status.includes("Production"))
                      badgeClass = "warning text-dark";
                    else if (status.includes("Completed"))
                      badgeClass = "info text-white";
                    else if (status.includes("Dispatched"))
                      badgeClass = "primary";
                    else if (status.includes("Cancelled"))
                      badgeClass = "danger";
                    else if (isEditLog) badgeClass = "info";
                    else if (status.includes("Updated"))
                      badgeClass = "secondary";

                    // Find matching edit history record
                    const matchingEdit = isEditLog ? editHistory.find((eh) => {
                      const editDate = new Date(eh.editedAt).getTime();
                      const logDate = new Date(log.changeDate).getTime();
                      return Math.abs(editDate - logDate) < 5000;
                    }) : null;

                    return (
                      <tr key={log.id}>
                        <td className="ps-4">
                          <span
                            className={`badge bg-${badgeClass} px-3 py-2 rounded-1`}
                          >
                            {isEditLog ? "Edited" : log.newStatus}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-person-fill me-2 text-dark"></i>
                              <span>{log.changedBy}</span>
                            </div>
                            {isEditLog && (
                              <div className="small text-muted mt-1">
                                <i className="bi bi-pencil me-1"></i>
                                {log.changedBy} edited this order
                                {matchingEdit && (
                                  <>
                                    {" — "}
                                    <a
                                      href="#"
                                      className="text-primary text-decoration-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewPreviousVersion(matchingEdit);
                                      }}
                                    >
                                      View previous version
                                    </a>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar-event me-2 text-dark"></i>
                            <span>
                              {new Date(log.changeDate).toLocaleString(
                                "en-GB",
                                {
                                  weekday: "long",
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Previous Version Modal */}
        <Modal show={showPreviousVersionModal} onHide={() => setShowPreviousVersionModal(false)} size="lg" centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="h5">
              <i className="bi bi-clock-history me-2"></i>
              Previous Order Version
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {previousVersionData ? (
              <div>
                {Object.entries(previousVersionData).map(([key, value]) => {
                  if (value === undefined || value === null || value === "") return null;
                  // Skip base64 image data
                  if (typeof value === "string" && value.startsWith("data:image")) return null;

                  const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

                  return (
                    <Row key={key} className="mb-2">
                      <Col md={4}>
                        <span className="fw-semibold text-muted small">{label}</span>
                      </Col>
                      <Col md={8}>
                        {Array.isArray(value) ? (
                          <div>
                            {value.map((item: any, idx: number) => (
                              <div key={idx} className="small">
                                {item.name || item.code || JSON.stringify(item)} {item.quantity ? `x ${item.quantity}` : ""}
                              </div>
                            ))}
                          </div>
                        ) : typeof value === "object" ? (
                          <span className="small">{JSON.stringify(value)}</span>
                        ) : (
                          <span className="small">{String(value)}</span>
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted text-center">No previous version data available.</p>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="secondary" size="sm" onClick={() => setShowPreviousVersionModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </PageContainer>
    </>
  );
};

export default OrderDetails;
